/**
 * The "what is going out" checklist every export dialog opens with.
 *
 * One component rather than one per dialog: pe3d exports variants, the
 * versionedPlayEngine exports versions and the snapshot editor exports
 * snapshot files, but the picking is the same picking — a search box, All and
 * None, folders that fold, and a count. Three copies of it drifted apart
 * within a week; the folder headings ended up smaller than the rows under
 * them in one of them and unindented in another.
 *
 * The caller owns what is ticked and what is shut. This draws them.
 *
 * The tree it takes is the shape every caller already has, or can build in a
 * few lines:
 *
 *   node   { path, name, count, children: node[], items: item[] }
 *   item   { id, label, note? }
 *
 * `path` identifies a folder for the closed set and must be unique; `count` is
 * what the heading shows, items included at every depth beneath it.
 */

import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { Button, Checkbox, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { BAR_BG, CONTROL_BG, CONTROL_BORDER, FIELD_LABEL } from "./snapshotEditor/panelPalette";

// One step per level, for folder headings and the rows alike, so a file and a
// subfolder at the same depth share a left edge.
const INDENT_STEP = 16;

const useStyles = makeStyles({
  caption: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: "11px",
    color: "#8a8d93",
    letterSpacing: "0.05em",
    marginBottom: "6px",
  },
  captionNote: { letterSpacing: 0 },
  link: { fontSize: "11px", minWidth: 0, padding: "0 4px", marginLeft: "4px", color: "#4eabb1", textTransform: "none" },
  filter: {
    marginBottom: "6px",
    backgroundColor: CONTROL_BG,
    border: `1px solid ${CONTROL_BORDER}`,
    borderRadius: "4px",
    padding: "0 8px",
  },
  list: {
    flex: 1,
    minHeight: "200px",
    overflowY: "auto",
    backgroundColor: CONTROL_BG,
    border: `1px solid ${CONTROL_BORDER}`,
    borderRadius: "4px",
  },
  empty: { padding: "12px 10px", fontSize: "12px", color: "#8a8d93" },
  // A folder heads the rows beneath it, so it reads larger than them, not
  // smaller: 11px muted over 13px white had the hierarchy upside down.
  folderRow: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    padding: "4px 8px 4px 2px",
    backgroundColor: BAR_BG,
    color: FIELD_LABEL,
    cursor: "pointer",
  },
  folderName: {
    flex: 1,
    minWidth: 0,
    fontSize: "14px",
    fontWeight: 600,
    color: "#f0f2f4",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  folderCount: { fontSize: "11.5px", fontWeight: 400, color: FIELD_LABEL },
  chevron: { flexShrink: 0, opacity: 0.6, fontSize: "11px" },
  itemRow: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    paddingRight: "10px",
    cursor: "pointer",
    "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
  },
  itemName: {
    flex: 1,
    minWidth: 0,
    fontSize: "13px",
    color: "#eee",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  itemNote: { fontSize: "11px", color: FIELD_LABEL, whiteSpace: "nowrap", flexShrink: 0 },
});

/** Every item id under a node, its subfolders included. */
export function idsUnder(node) {
  return [...node.items.map((item) => item.id), ...node.children.flatMap(idsUnder)];
}

export default function ExportPickerList({
  caption,
  tree,
  tickedIds,
  onTicked,
  total,
  query,
  onQuery,
  searchPlaceholder,
  closedPaths,
  onToggleFolder,
  disabled,
  emptyText,
  itemRef,
}) {
  const classes = useStyles();
  const ticked = new Set(tickedIds);
  const isSearching = Boolean(String(query || "").trim());

  function renderFolder(node, depth) {
    const ids = idsUnder(node);
    const allOn = ids.length > 0 && ids.every((id) => ticked.has(id));
    const someOn = !allOn && ids.some((id) => ticked.has(id));
    // A search result is useless behind a shut folder.
    const shut = !isSearching && closedPaths.includes(node.path);

    return (
      <div key={node.path}>
        <div
          className={classes.folderRow}
          style={{ paddingLeft: 2 + depth * INDENT_STEP }}
          onClick={() => onToggleFolder(node.path)}
        >
          <Checkbox
            size="small"
            color="primary"
            checked={allOn}
            indeterminate={someOn}
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              if (disabled) return;
              onTicked(allOn ? tickedIds.filter((id) => !ids.includes(id)) : _.uniq([...tickedIds, ...ids]));
            }}
          />
          <span className={classes.folderName}>
            {node.name} <span className={classes.folderCount}>· {node.count}</span>
          </span>
          <span className={classes.chevron}>{shut ? "▾" : "▴"}</span>
        </div>
        {!shut && node.children.map((child) => renderFolder(child, depth + 1))}
        {!shut && node.items.map((item) => renderItem(item, depth + 1))}
      </div>
    );
  }

  function renderItem(item, depth) {
    return (
      <div
        key={item.id}
        ref={itemRef ? (node) => itemRef(item.id, node) : undefined}
        className={classes.itemRow}
        style={{ paddingLeft: 2 + depth * INDENT_STEP }}
        onClick={() =>
          !disabled &&
          onTicked(ticked.has(item.id) ? tickedIds.filter((id) => id !== item.id) : [...tickedIds, item.id])
        }
      >
        <Checkbox size="small" color="primary" checked={ticked.has(item.id)} disabled={disabled} />
        <span className={classes.itemName}>{item.label}</span>
        {item.note ? <span className={classes.itemNote}>{item.note}</span> : null}
      </div>
    );
  }

  const isEmpty = tree.items.length === 0 && tree.children.length === 0;

  return (
    <>
      <div className={classes.caption}>
        <span>{caption}</span>
        <span>
          <span className={classes.captionNote}>
            {tickedIds.length} of {total} ticked
          </span>
          <Button
            size="small"
            className={classes.link}
            disabled={disabled}
            onClick={() => onTicked(_.uniq([...tickedIds, ...idsUnder(tree)]))}
          >
            {isSearching ? "All shown" : "All"}
          </Button>
          <Button size="small" className={classes.link} disabled={disabled} onClick={() => onTicked([])}>
            None
          </Button>
        </span>
      </div>
      <TextField
        size="small"
        fullWidth
        placeholder={searchPlaceholder}
        value={query}
        disabled={disabled}
        className={classes.filter}
        InputProps={{ disableUnderline: true, style: { fontSize: 13, color: "#eee" } }}
        onChange={(event) => onQuery(event.target.value)}
      />
      <div className={classes.list}>
        {isEmpty && <div className={classes.empty}>{emptyText}</div>}
        {/* Loose rows lead, folders follow. The other way round they sit
            directly under the last folder's heading and read as being inside
            it, which is exactly what they are not. */}
        {tree.items.map((item) => renderItem(item, 0))}
        {tree.children.map((child) => renderFolder(child, 0))}
      </div>
    </>
  );
}

ExportPickerList.propTypes = {
  caption: PropTypes.string.isRequired,
  tree: PropTypes.object.isRequired,
  tickedIds: PropTypes.array.isRequired,
  onTicked: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
  query: PropTypes.string.isRequired,
  onQuery: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string,
  closedPaths: PropTypes.array.isRequired,
  onToggleFolder: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  emptyText: PropTypes.string,
  itemRef: PropTypes.func,
};
